import { Component, OnInit } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import {
  SearchQuery,
} from '../../../../../../../../server/dist/angular/server/src/microservice/models/search-query.model';
import { TableStateEnum } from '../../../core/components/table/table-state.enum';
import { CalculationsApiController } from '../../api-controllers/calculations.api-controller';
import { ActivatedRoute, Router } from '@angular/router';
import { CalculationStatusEnum } from '../../../../../../../../common/src/enums/calculations/calculation-status.enum';
import { CalculationKeynameEnum } from '../../../../../../../../common/src/enums/calculations/calculation-keyname.enum';
import { CalculationsUtils } from '../../utils/calculations.utils';
import { DeleteCalculationComponent } from '../../components/delete-calculation/delete-calculation.component';
import { DuplicateCalculationComponent } from '../../components/duplicate-calculation/duplicate-calculation.component';
import { take } from 'rxjs/operators';
import {
  CalculationCreationOptions,
} from '../../../../../../../../common/src/options/calculations/calculation-creation.options';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CalculationStore } from '../../stores/calculation.store';
import { CalculationIndexModel } from '../../../../../../../../common/src/index-models/calculation.index-model';
import { SearchResult } from '../../../../../../../../common/src/models/search-result.model';
import {
  SearchQueryField,
} from '../../../../../../../../server/dist/angular/server/src/microservice/models/search-query-field.model';
import { SortOrderEnum } from '../../../../../../../../server/dist/angular/server/src/indexing/enums/sort-order.enum';
import { AlertMessageStore } from '../../../core/stores/alert-message.store';
import { AlertMessageModel } from '../../../core/models/alert-message.model';
import { SeverityTypeEnum } from '../../../core/enums/severity-type.enum';
import { AlertContextEnum } from '../../../core/enums/alert-context.enum';

@Component({
  selector: 'app-calculation-list',
  templateUrl: './calculation-list.component.html',
  styleUrls: ['./calculation-list.component.scss'],
  standalone: false,
})
export class CalculationListComponent implements OnInit {

  isLoading = false;

  showHasBeenRefreshed = false;

  searchResult?: SearchResult<CalculationIndexModel>;

  searchQuery = new SearchQuery();

  totalNumberOfResults = 0;

  numberOfResultsReturned = 0;

  page = 1;

  maximumNumberOfResultsPerPage = 100;

  columns: { displayText: string, keyname: string, isSortable: boolean }[] = [
    { displayText: 'Titre', keyname: 'title', isSortable: true },
    { displayText: 'Type de calcul', keyname: 'calculationKeyname', isSortable: false },
    { displayText: 'Status', keyname: 'status', isSortable: false },
    { displayText: 'Dernière modification', keyname: 'updatedAt', isSortable: true },
    { displayText: 'Actions', keyname: 'actions', isSortable: false },
  ];

  tableState: TableStateEnum = TableStateEnum.Loading;

  TableStateEnum = TableStateEnum;

  constructor(public readonly authService: AuthService,
              private readonly calculationsApiController: CalculationsApiController,
              public readonly router: Router,
              public readonly route: ActivatedRoute,
              private readonly calculationsUtils: CalculationsUtils,
              private readonly calculationStore: CalculationStore,
              private readonly modalService: NgbModal,
              private readonly alertMessageStore: AlertMessageStore,
  ) {
  }

  async ngOnInit() {
    this.searchQuery.fields.push(new SearchQueryField('updatedAt', {
      field: 'updatedAt',
      order: SortOrderEnum.Descending,
    }));
    this.searchQuery.maximumNumberOfResultsPerPage = this.maximumNumberOfResultsPerPage;
    await this.refresh();
  }

  async refresh() {
    if (this.searchResult === undefined) {
      this.tableState = TableStateEnum.Loading;
    } else {
      this.tableState = TableStateEnum.Refreshing;
    }

    try {
      this.searchResult = await this.calculationsApiController.search(this.searchQuery);

      this.totalNumberOfResults = this.searchResult.totalNumberOfResults;
      this.numberOfResultsReturned = this.searchResult.numberOfResultsReturned;
      this.page = this.searchResult.page;

      if (this.searchResult.totalNumberOfResults === 0) {
        this.tableState = TableStateEnum.NoData;
      } else {
        this.tableState = TableStateEnum.Loaded;
      }
    } catch (error) {
      this.alertMessageStore.save(new AlertMessageModel(SeverityTypeEnum.Error, AlertContextEnum.Calculations, {
        fr: 'Une erreur est survenue lors de la récupération des calculs.',
        en: 'An error occurred while fetching the calculations.',
      }));
    }


  }

  getResults(): CalculationIndexModel[] {
    if (this.searchResult === undefined) {
      return [];
    }

    return this.searchResult.results;
  }

  async calculationClicked(calculation: CalculationIndexModel) {
    return await this.router.navigate([calculation.calculationKeyname, calculation.id], { relativeTo: this.route });
  }

  getStatusText(status: CalculationStatusEnum) {
    return this.calculationsUtils.getStatusText(status);
  }

  getKeynameText(calculationKeyname: CalculationKeynameEnum) {
    return this.calculationsUtils.getCalculationKeynameText(calculationKeyname);
  }

  getLastModifiedText(calculationResponse: CalculationIndexModel) {
    const lastModified = this.calculationsUtils.getLastModifiedDate(calculationResponse);
    return this.calculationsUtils.getLastModifiedText(lastModified);
  }

  async tryDelete(calculationId: string): Promise<void> {
    const modal = this.modalService.open(DeleteCalculationComponent);
    modal.closed.subscribe(async result => {
      if (result === true) {
        await this.delete(calculationId);
        return;
      }
    });
  }

  async delete(calculationId: string): Promise<void> {
    await this.calculationStore.delete(calculationId);
    await this.refresh();
  }

  async tryDuplicate(calculationId: string): Promise<void> {
    const modal = this.modalService.open(DuplicateCalculationComponent);
    modal.closed.subscribe(async result => {
      if (result === true) {
        await this.duplicate(calculationId);
        return;
      }
    });
  }

  async duplicate(calculationId: string): Promise<void> {
    const calculation = await this.calculationStore.get(calculationId).pipe(take(1)).toPromise();
    const options = new CalculationCreationOptions();
    options.createFromCalculationId = calculationId;
    options.calculationKeyname = calculation.calculationKeyname;
    options.title = calculation.title + ' - Copy';
    await this.calculationStore.create(options);
    await this.refresh();
  }

}
